'use client'
import * as React from 'react'
import { useRegisterMutation } from 'codegen/generated/user'
import { ChallengeType } from 'codegen/types'
import type { UserAuthResponse } from '@repo/auth-utils'
import { getDeviceId, saveToken } from '@repo/auth-utils'
import { LockFasIcon } from 'primitives/icons/security'
import { SpinnerIcon } from 'primitives/icons'
import * as Form from 'primitives/form'
import { isError } from 'utils/js'
import { useTranslations } from 'next-intl'
import { isLoggedInSelector } from 'auth/selectors'
import { getCookie } from 'utils/dom'
import { STUDIO_REFERRAL_CODE_COOKIE_NAME } from 'constants/cookies'

import { Divider } from '../divider'
import { useUserAuthModal } from './user-auth-dialog-context'

export function RegisterOtp({ onSuccess }: { onSuccess(user: UserAuthResponse): void }) {
  const t = useTranslations('register_otp')
  const { formState, setStep, setErrorMessage } = useUserAuthModal()

  const registerMutation = useRegisterMutation({
    onSuccess: async function onSuccessCallback(data) {
      if (data.register?.token) {
        await saveToken(data.register.token, 'registration').catch(() => {
          throw new Error('CF_INVALID_TOKEN')
        })
        onSuccess(data.register)
        return
      }

      if (data.register?.challenge?.type === ChallengeType.ChallengeOtp) {
        return
      }

      if (data.register?.errors?.find(err => err.message === 'otp')) {
        throw new Error('CF_INVALID_OTP')
      }

      throw new Error('UNKNOWN_REGISTER_OTP_ERROR')
    },
  })

  const ref = React.useRef<HTMLFormElement>(null)
  const [otp, setOtp] = React.useState(formState?.get('otp')?.toString() ?? '')
  const isAutoSubmit = React.useRef(false)

  function performRegister(otpValue: string) {
    const email = formState?.get('email')?.toString() ?? ''
    const password = formState?.get('password')?.toString() ?? ''
    const terms = formState?.get('terms')?.toString() === 'on'
    const deviceId = getDeviceId()

    let referralCode = ''
    try {
      referralCode = getCookie(STUDIO_REFERRAL_CODE_COOKIE_NAME)
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error('Failed to read referral code cookie:', error)
    }

    registerMutation.mutate({
      input: {
        email,
        password,
        otp: otpValue,
        deviceId,
        terms,
        referral_code: referralCode,
      },
    })
  }

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    isAutoSubmit.current = false
    performRegister(otp)
  }

  function handleOtpChange(e: React.ChangeEvent<HTMLInputElement>) {
    const newValue = e.target.value
    setOtp(newValue)
    setErrorMessage(null)

    if (newValue.length === 4) {
      isAutoSubmit.current = true
      performRegister(newValue)
    }
  }

  function onResendOTP(e: React.MouseEvent<HTMLAnchorElement>) {
    e.preventDefault()

    if (registerMutation.isPending) {
      return null
    }

    const email = formState?.get('email')?.toString() ?? ''
    const password = formState?.get('password')?.toString() ?? ''
    const terms = formState?.get('terms')?.toString() === 'on'
    const deviceId = getDeviceId()

    let referralCode = ''
    try {
      referralCode = getCookie(STUDIO_REFERRAL_CODE_COOKIE_NAME)
    } catch (error) {
      //eslint-disable-next-line no-console
      console.error('Failed to read referral code cookie:', error)
    }

    registerMutation.mutate(
      {
        input: {
          email,
          password,
          deviceId,
          terms,
          referral_code: referralCode,
        },
      },
      {
        onSuccess(data) {
          if (data?.register?.challenge?.otp) {
            const element = ref.current?.querySelector('#otp')
            if (element instanceof HTMLInputElement) {
              element.value = data.register.challenge.otp.toString()
            }
          }
        },
      },
    )
  }

  const email = formState?.get('email')?.toString()

  React.useEffect(() => {
    function resolveErrorMessage() {
      if (registerMutation.isPending || !registerMutation.isError) {
        return null
      }

      if (registerMutation.isPaused) {
        setErrorMessage(t('errors.offline'))
        return
      }

      if (isError(registerMutation.error) && registerMutation.error.message === 'CF_INVALID_OTP') {
        if (isAutoSubmit.current) {
          return
        }
        setErrorMessage(
          t.rich('errors.invalid_details', {
            resend: str => (
              <span
                onClick={onResendOTP}
                className="text-14 cursor-pointer font-bold text-[#ffffff] underline hover:text-[#ffffff]"
              >
                {str}
              </span>
            ),
          }),
        )
        return
      }

      setErrorMessage(t.rich('errors.general', { br: () => <br /> }))
    }

    resolveErrorMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    registerMutation.isError,
    registerMutation.isPending,
    registerMutation.isPaused,
    registerMutation.error,
  ])

  const disabled = registerMutation.isPending || isLoggedInSelector(registerMutation.data?.register)

  return (
    <div className="flex w-full flex-col">
      <h2 className="text-20 mb-4 text-center font-bold">{t('title')}</h2>
      <span className="text-14 mt-[5px] text-center">
        {t.rich('copy', {
          email: str => {
            if (email) {
              return <span className="font-bold">{email}</span>
            }

            return str
          },
        })}
      </span>
      <span
        onClick={() => {
          setStep('login')
        }}
        className="text-14 cursor-pointer text-center font-bold text-[#0a5ea8] underline hover:text-secondary-500"
      >
        {t('change_email')}
      </span>

      <Form.Root ref={ref} onSubmit={onSubmit} className="mt-[5px] flex w-full flex-col gap-4">
        <Form.Field name="otp">
          <Form.Label className="text-12 align-text-top">{t('otp')} *</Form.Label>
          <div className="relative flex w-full rounded border border-[#e0e2ea] pl-[10px]">
            <div className="pointer-events-none absolute inset-y-0 flex items-center">
              <LockFasIcon className="h-[14px] w-[14px] text-[#e3e1e9]" />
            </div>
            <Form.Control asChild>
              <input
                required
                value={otp}
                onChange={handleOtpChange}
                type="otp"
                name="otp"
                id="otp"
                className="text-16 lg:text-14 h-[38px] w-full p-3 pl-7 outline-none"
                autoFocus
                disabled={disabled}
              />
            </Form.Control>
          </div>
        </Form.Field>

        <Form.Submit asChild>
          <button
            disabled={disabled}
            className="text-14 mt-2 flex h-[45px] w-full items-center justify-center rounded bg-secondary-500 font-bold uppercase text-[#ffffff] transition-[background-color] hover:bg-secondary-700"
          >
            {disabled ? <SpinnerIcon className="h-[24px] w-[24px] animate-spin" /> : t('cta')}
          </button>
        </Form.Submit>
      </Form.Root>
      <Divider className="mt-2 bg-[#DFDDE5]" />
      <div className="text-14 m-auto mt-4">
        {t.rich('resend_otp', {
          resendOtp: str => (
            <span
              onClick={onResendOTP}
              className="text-14 cursor-pointer font-bold text-[#0a5ea8] underline hover:text-secondary-500"
            >
              {str}
            </span>
          ),
        })}
      </div>
    </div>
  )
}
